import React, { useState, useEffect } from 'react';
import { Box, Stepper, Step, StepLabel, Typography, Alert, StepButton, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import MagicLinkSignupPage from '../User/MagicLinkSignupPage';
import StreamTargetPage from '../StreamTarget/StreamTargetPage';
import EventPage from '../Events/EventPage';
import Page from '../../components/Page/Page';
import useGTM from '../../packages/useGTM/useGTM';
import Confetti from 'react-confetti';
import { useWindowSize } from 'react-use';

const OnboardingFlow = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const gtm = useGTM({});
    const { width, height } = useWindowSize();

    // Initialize activeStep from URL or default to 0
    const initialStep = parseInt(searchParams.get('step') || '0', 10);
    const [activeStep, setActiveStep] = useState(initialStep);
    const [showConfetti, setShowConfetti] = useState(false);

    const steps = [
        t('Signup'),
        t('Add Social Networks'),
        t('Create Event')
    ];

    useEffect(() => {
        // Update URL when activeStep changes
        setSearchParams({ step: activeStep.toString() });

        // Track step view
        gtm.trackEvent('onboarding_step_view', {
            step: activeStep,
            stepName: steps[activeStep]
        });
    }, [activeStep, setSearchParams, gtm, steps]);

    const handleSignupSuccess = () => {
        gtm.trackEvent('onboarding_step_complete', { step: 0, stepName: 'Signup' });
        setActiveStep(1);
    };

    const handleSocialContinue = () => {
        gtm.trackEvent('onboarding_step_complete', { step: 1, stepName: 'Add Social Networks' });
        setActiveStep(2);
    };

    const handleSocialSkip = () => {
        gtm.trackEvent('onboarding_skip', { step: 1, stepName: 'Add Social Networks' });
        setActiveStep(2);
    };

    // We need to detect when the event is created to show confetti.
    // Since EventPage doesn't have an onSuccess prop yet, and modifying it might be complex due to its internal logic,
    // we can try to wrap it or just show confetti if the user reaches a "success" state.
    // However, EventPage handles the "Create" button click internally.
    // For now, let's assume if they are on step 2, they are creating an event.
    // If we want to show confetti AFTER creation, we ideally need a callback.
    // But the user asked for "Celebration/Confetti: When the user completes the flow."
    // Completing the flow means finishing step 2.
    // Since we can't easily hook into EventPage's success without modifying it significantly (which might break other things),
    // and EventPage redirects to /events/:id or shows a dialog, maybe we can show confetti 
    // if we detect a successful creation?
    // Or maybe we can just show it when they reach the dashboard? No, that's outside this flow.

    // Let's look at EventPage again. It seems it doesn't take an onSuccess.
    // But wait, the user said "When the user completes the flow".
    // If EventPage redirects, the flow component unmounts.
    // So confetti should probably be on the destination page OR we intercept the success.

    // Given the constraints and the request to modify EventPage in the plan, let's assume we CAN modify EventPage.
    // But I'll stick to OnboardingFlow changes first.
    // If I can't modify EventPage easily to accept a callback, I might be stuck.
    // Let's check EventPage again.

    // Actually, for now, let's implement the logic in OnboardingFlow and pass a dummy onSuccess to EventPage if we modify it,
    // or just show confetti if we can. 
    // But wait, if EventPage redirects, we won't see confetti.
    // Maybe we can show confetti on the LAST step (Create Event) as a "You are almost there!"?
    // No, "When the user completes the flow".

    // Let's assume we will modify EventPage to accept `onSuccess`.
    const handleEventSuccess = () => {
        gtm.trackEvent('onboarding_complete', {});
        setShowConfetti(true);
        // Delay navigation to let confetti show?
        // Or just show confetti and let the user click "Go to Studio" in the dialog EventPage might show?
        // If EventPage shows a dialog, we can show confetti behind it.
    };

    const handleStepClick = (index: number) => {
        if (index < activeStep) {
            setActiveStep(index);
        }
    };

    const getStepContent = (step: number) => {
        switch (step) {
            case 0:
                return <MagicLinkSignupPage onSuccess={handleSignupSuccess} />;
            case 1:
                return (
                    <Box>
                        <Alert severity="info" sx={{ mb: 2 }}>
                            {t('This is the most complex step, but do not worry, you only have to do it once. Please be patient.')}
                        </Alert>
                        <StreamTargetPage onContinue={handleSocialContinue} onSkip={handleSocialSkip} />
                    </Box>
                );
            case 2:
                return (
                    <Box>
                        <Button onClick={() => setActiveStep(1)} sx={{ mb: 2 }}>
                            {t('Back')}
                        </Button>
                        <EventPage eventCode="" onSuccess={handleEventSuccess} />
                    </Box>
                );
            default:
                return 'Unknown step';
        }
    };

    return (
        <Page title={t('Welcome to Live4.tv')}>
            {showConfetti && <Confetti width={width} height={height} />}
            <Box sx={{ width: '100%', p: 3 }}>
                <Typography variant="h5" align="center" gutterBottom>
                    {t('Only 3 simple steps to start streaming everywhere')}
                </Typography>
                <Box sx={{ width: '100%', my: 4 }}>
                    <Stepper activeStep={activeStep} nonLinear>
                        {steps.map((label, index) => (
                            <Step key={label}>
                                <StepButton onClick={() => handleStepClick(index)} disabled={index > activeStep}>
                                    {label}
                                </StepButton>
                            </Step>
                        ))}
                    </Stepper>
                </Box>
                <Box sx={{ width: '100%' }}>
                    {getStepContent(activeStep)}
                </Box>
            </Box>
        </Page>
    );
};

export default OnboardingFlow;
